/**
 * An Integral Delta analysis, as text for the Study Journal.
 *
 * <h2>The Journal, not a pop-up</h2>
 *
 * A pop-up is the wrong container for this. The output is a table over possibly hundreds of trades,
 * and the whole reason to run it is to compare — against yesterday's run, against another symbol,
 * against a different set of ATR multiples. A modal is gone the moment it is dismissed and cannot
 * be compared with anything. The Journal persists, is exportable, and already
 * has a convention for exactly this: `handleStudyReportJournal` appends, names the study, switches
 * to the Journal tab and opens the panel, because a study whose only output is the Journal
 * otherwise appears to have done nothing.
 *
 * <p>The one-line headline goes to the status bar as well, so the answer is visible without
 * reading the panel — but the status bar is transient and cannot hold a table, which is the
 * division of labour between the two.
 *
 * <h2>Monospace, and therefore aligned</h2>
 *
 * The Journal renders `reportText` inside a fenced block, so columns line up if they are padded
 * and turn to porridge if they are not. The exit breakdown is only readable as a table.
 *
 * <h2>AGGREGATES ONLY. No per-row listings.</h2>
 *
 * This report used to end with the most recent 25 trades, the most recent 12 signals, and five PTA
 * and five PTB level intervals with their highs, lows and bar spans. All of it is gone, and the
 * reason is what the Journal is FOR: comparison, between runs and between symbols. Fifty rows of
 * individual detail pushed every comparable number off the top of the panel, and a thousand-trade
 * run cannot be read a row at a time anyway.
 *
 * <p>The per-item detail lives on the CHART, where a marker carries its own numbers on hover beside
 * the bars that produced them - which is the only place that detail means anything. What survives
 * here is every number a reader would put next to yesterday's, plus the handful of lines that are
 * warnings rather than data.
 */

/** Percent, or an em dash where there is nothing to divide. */
const pct = v => (v === null || v === undefined || Number.isNaN(v) ? '—' : `${v.toFixed(1)}%`);

/** Two decimals, or an em dash. Profit factor is null when nothing was lost. */
const num = (v, dp = 2) =>
  (v === null || v === undefined || Number.isNaN(v) ? '—' : v.toFixed(dp));

const pad = (s, n) => String(s).padStart(n);
const padEnd = (s, n) => String(s).padEnd(n);

/** How many signals came from each rule — the breakdown a net figure cannot give. */
export function countsBySource(signals) {
  const out = {};
  (Array.isArray(signals) ? signals : []).forEach((s) => {
    const key = s?.source || 'UNKNOWN';
    out[key] = (out[key] || 0) + 1;
  });
  return out;
}

/** The three mechanics the dialog offers, in the order it offers them. */
export const ALL_MECHANICS = ['CTS', 'PTA', 'PTB'];

/**
 * Whether a mechanic ran, according to the SERVER.
 *
 * <p>`report.mechanics` is what the engine was told, not what the dialog asked for, and the two
 * can disagree: a backend that predates the filter accepts `signals`, ignores it, and returns a
 * complete run. Reading the request instead would caption that run "PTA only" — confidently wrong.
 *
 * <p>Also true for a partial selection, where the server names individual sources
 * (`PTA_CROSS`) rather than the mechanic, so the annotations stay right if a caller ever asks for
 * one half of a pillar.
 */
export function ranMechanic(report, name) {
  const list = Array.isArray(report?.mechanics) ? report.mechanics : null;
  if (!list) return true;   // unknown: an old backend ran everything
  return list.includes(name) || list.some(m => String(m).startsWith(`${name}_`));
}

/** The mechanics line, or null when the backend did not say. */
export function mechanicsLine(report) {
  const list = Array.isArray(report?.mechanics) ? report.mechanics : null;
  if (!list) {
    // NOT silence. A dialog selection that the server ignored produces a complete run whose only
    // symptom is that every count is bigger than expected - which is indistinguishable from a busy
    // symbol. Saying so is the difference between a stale backend and a mysterious result.
    return 'Mechanics: not reported — this backend predates the signal filter, so ALL '
      + 'mechanics ran regardless of the dialog.';
  }
  const excluded = ALL_MECHANICS.filter(m => !ranMechanic(report, m));
  if (excluded.length === 0) return `Mechanics: ${list.join(', ')} (all)`;
  return `Mechanics: ${list.join(', ')} — ${excluded.join(' and ')} excluded by request`;
}

/**
 * One line for the status bar: the shortest true statement about the run.
 *
 * <p>Carries the average R rather than only the net, because a net figure without the risk taken
 * for it is the number a backtest summary most easily flatters itself with.
 *
 * <p>And the restriction, when there is one. Every figure on this line is SMALLER under a filter,
 * and smaller is exactly what a quiet symbol looks like — so a restricted run that does not say it
 * is restricted reads as a bad result rather than a narrow question.
 */
export function headline(report) {
  if (!report || !report.summary) return 'Integral Delta: nothing to report.';
  const s = report.summary;
  const list = Array.isArray(report.mechanics) ? report.mechanics : null;
  const excluded = list ? ALL_MECHANICS.filter(m => !ranMechanic(report, m)) : [];
  const scope = excluded.length > 0 ? ` [${list.join('+')} only]` : '';
  return `Integral Delta ${report.symbol}${scope}: ${(report.signals || []).length} signals over `
    + `${report.barCount} bars — ${s.trades} trades, ${pct(s.winRatePct)} win, `
    + `${num(s.avgRMultiple)}R average, ${num(s.netPercentageGain)}% on a fixed stake.`;
}

/**
 * The exits, in the order the state machine reaches them.
 *
 * <p>There is no TIME_STOP, BREAKEVEN_STOP or TRAILING_STOP. The clock fired on exactly the trades
 * that had gone nowhere YET — the same set as the trades still coiling. The breakeven and trailing
 * stops went with the trail itself: a fixed distance behind the best price is a horizontal measure,
 * so it could not tell a retracement inside an advance from the end of one, and it was taking
 * trades out on ordinary pullbacks.
 */
export const EXIT_ORDER = [
  ['INITIAL_STOP', 'hit the 1%-of-entry stop; never formed a swing'],
  ['BREAKEVEN_STOP', 'gave back a 1R gain to the stop at entry'],
  ['MFE_TRAIL', 'reached 2R, then gave back half the excursion'],
  ['TRENDLINE_EXIT', 'rode the line, then closed through it'],
  ['SR_TARGET', 'turned back from a PTA/PTB level'],
  ['FIB_EXTENSION_TARGET', 'reached the extension of the leg it was continuing'],
  ['NEXT_CTS_POINT', 'held until the next CTS point printed'],
  ['MIDPOINT_REVERSAL', 'the mid-point of a CTS pair'],
  ['CTS_PAIR_CLOSE', "the pair's right-hand point"],
  ['END_OF_DATA', 'still open when the data ended'],
];

/**
 * The full report, as a monospace block.
 *
 * @param {object} report the AnalysisReport as the backend serialises it
 * <p>It takes no sample sizes any more. `sampleSignals` and `sampleTrades` sized the two per-row
 * listings this report used to end with; with the listings gone they would be parameters that
 * accept a value and change nothing, which is worse than no parameter at all - tuning one looks
 * like it should work.
 */
/**
 * The DynaMan comparison block.
 *
 * <p>An unavailable row says WHY rather than printing zeros. A provider whose dataset could not
 * be reached and a provider that found nothing both produce "0 trades", and only the second is a
 * finding — `datasets/` is excluded from the Cloud Run source upload, so on a deployed backend
 * the TrendPulse and Confluence rows depend on the public bucket being reachable.
 */
export function dynaManModeLines(modes, error) {
  const out = ['', 'DynaMan engine, same symbol (standalone providers, structural entry off):'];

  if (error) {
    out.push(`  unavailable — ${error}`);
    return out;
  }
  if (!Array.isArray(modes) || modes.length === 0) {
    out.push('  not run.');
    return out;
  }

  const width = Math.max(...modes.map(m => ((m && (m.label || m.mode)) || '').length));
  modes.forEach((m) => {
    if (!m) return;
    const name = padEnd(m.label || m.mode, width);
    if (m.note) {
      out.push(`  ${name}  unavailable — ${m.note}`);
      return;
    }
    // "n/a", never a large number: an absent denominator is not a perfect score, and the engine
    // and the CSV export already make the same call.
    const pf = Number.isFinite(m.profitFactor) ? num(m.profitFactor) : 'n/a';
    out.push(`  ${name}  ${pad(m.trades, 5)} trades`
      + `  ${pad(`${m.wins}/${m.losses}`, 8)}`
      + `  win ${pad(num(m.winRatePercent, 1), 5)}%`
      + `  PF ${pad(pf, 6)}`
      + `  net ${pad(num(m.totalNetPnlPercent, 1), 8)}%`
      + `  maxDD ${num(m.maxDrawdownPercent, 1)}%`);
  });
  return out;
}

export function formatAnalysisReport(report) {
  if (!report) return 'Integral Delta: no report.';
  const lines = [];
  const signals = Array.isArray(report.signals) ? report.signals : [];
  const s = report.summary || {};

  lines.push(`Integral Delta — ${report.symbol}`);
  lines.push(`${report.barCount} bars analysed`);
  // THIRD LINE, not a footnote. It decides what every number below means, and it is the one thing
  // a reader cannot recover from the numbers themselves.
  lines.push(mechanicsLine(report));
  lines.push('');

  // ── signals ───────────────────────────────────────────────────────────────────────────────
  const counts = countsBySource(signals);
  lines.push(`Signals: ${signals.length}`);
  Object.keys(counts).sort().forEach((k) => {
    lines.push(`  ${padEnd(k, 16)} ${pad(counts[k], 5)}`);
  });
  // WHAT WAS WITHHELD. A signal generated and then filtered leaves no trace otherwise, and from
  // the outside that is indistinguishable from one the engine never found — which is precisely how
  // "valid setups are being dropped" gets reported about a filter doing its job.
  if (report.lockedOutReentries > 0) {
    lines.push(`  ${padEnd('re-entries held', 16)} ${pad(report.lockedOutReentries, 5)}`
      + ' (anti-chop cooldown: same boundary, same direction)');
  }
  if (report.mergedSameBarEntries > 0) {
    // A merge is the engine DECLINING to take a position it would otherwise have taken, which
    // makes it the most consequential of these three counts and the one most worth seeing.
    lines.push(`  ${padEnd('same-bar merged', 16)} ${pad(report.mergedSameBarEntries, 5)}`
      + ' (converging bands: one trade, not two, at one unit of risk)');
  }
  if (report.crossesSuppressedByAdx > 0) {
    lines.push(`  ${padEnd('ADX-suppressed', 16)} ${pad(report.crossesSuppressedByAdx, 5)}`);
  }
  // THE INTERVAL COUNTS, all that is left of two blocks that listed five scopes each with their
  // highs, lows and bar spans. The rows are gone at the reader's request; these two lines are not
  // rows, and dropping them outright would have taken two facts with them:
  //
  //   ZERO IS A DIFFERENT STATEMENT FROM SILENCE. A window only contains the intervals that
  //   COMPLETE inside it - Venus and Mercury take months to cross a sign - so a short range
  //   legitimately yields no levels and therefore no geometry signals. Without this line that
  //   reads as the geometry having broken.
  //
  //   AN EXCLUDED MECHANIC STILL HAS LEVELS IN FORCE. A PTA line goes on being an exit target for
  //   a CTS trade whether or not PTA was allowed to open one, so an unannotated zero would be
  //   wrong in the other direction.
  //
  // One line each, beside the other aggregates, which is where a number a reader compares belongs.
  const intervalLine = (name, scopes) => {
    const n = Array.isArray(scopes) ? scopes.length : 0;
    const note = !ranMechanic(report, name)
      ? ' (excluded from this run — still in force as exit targets)'
      : (n === 0 ? ' (none completed inside this range)' : '');
    lines.push(`  ${padEnd(`${name} intervals`, 16)} ${pad(n, 5)}${note}`);
  };
  intervalLine('PTA', report.ptaScopes);
  intervalLine('PTB', report.ptbScopes);

  const cts = Array.isArray(report.ctsAccepted) ? report.ctsAccepted.length : 0;
  // The CTS count survives its own exclusion for the same reason the level blocks do - the points
  // were resolved either way - so it says which it is. Unlabelled, "CTS points kept 143" in a run
  // with CTS switched off was the single most misleading line in this report.
  // THE DATE-LOCK, VERIFIED FROM THE DATA RATHER THAN TRUSTED. Every CTS signal must sit on a day
  // the ephemeris actually named; a non-zero count here means one was invented, which is the
  // reported "CTS momentum on a day that is not a CTS point". Computed from two fields already on
  // the wire, so it needs no counter and cannot disagree with the engine.
  const pointDays = new Set(Array.isArray(report.ctsAccepted) ? report.ctsAccepted : []);
  const offPoint = signals
    .filter(x => x?.source === 'CTS_EXHAUSTION' && !pointDays.has(x.date)).length;
  const ctsNote = ranMechanic(report, 'CTS')
    ? '(after snapping and clustering)'
    : '(CTS excluded from this run — resolved, but none were traded)';
  lines.push(`  ${padEnd('CTS points kept', 16)} ${pad(cts, 5)} ${ctsNote}`);
  if (offPoint > 0) {
    lines.push(`  ${padEnd('!! off a point day', 16)} ${pad(offPoint, 5)}`
      + ' — a CTS signal on a date the ephemeris did not name');
  }
  lines.push('');

  // ── THE RULES ARE NOT RESTATED HERE ──────────────────────────────────
  //
  // Five lines of prose used to describe the trade management: the 1% stop, the swing trendline,
  // the 1R breakeven, the 2R trail. Gone at the reader's request, and it costs nothing, because
  // EXIT_ORDER already says all of it - per exit, beside the count of trades that ended that way,
  // which is where the description is actually load-bearing:
  //
  //   INITIAL_STOP     hit the 1%-of-entry stop; never formed a swing
  //   BREAKEVEN_STOP   gave back a 1R gain to the stop at entry
  //   MFE_TRAIL        reached 2R, then gave back half the excursion
  //   TRENDLINE_EXIT   rode the line, then closed through it
  //
  // The prose predated those descriptions and was never updated to notice them, which is why it
  // was still claiming "a 1.5-ATR origin shield" long after the stop had become a percentage: two
  // statements of one ruleset, and nothing read the one that was wrong.
  //
  // THE ONE THING THAT DOES NOT GO WITH IT is the ATR guard. "ATR" must not appear anywhere in this
  // report - the stop is a percentage of the entry, and was for months while the report said
  // otherwise - so the test that asserted its absence from this block now asserts it over the
  // WHOLE output, which is a stronger claim and the one that was always meant.
  lines.push(`  ${padEnd('trades', 22)} ${pad(s.trades, 9)}`
    + `   (${s.longs} long, ${s.shorts} short)`);
  lines.push(`  ${padEnd('wins / losses', 22)} ${pad(`${s.wins} / ${s.losses}`, 9)}`
    + `   ${pct(s.winRatePct)}`);
  lines.push(`  ${padEnd('gross gain', 22)} ${pad(num(s.grossGain), 9)}`);
  lines.push(`  ${padEnd('gross loss', 22)} ${pad(num(s.grossLoss), 9)}`);
  lines.push(`  ${padEnd('net', 22)} ${pad(num(s.net), 9)}`);
  // The fixed-capital return, and the figure to compare between symbols. `net` above sums PRICE
  // differences, so over a history whose price level changed 100x it is dominated by the recent
  // end and can read positive while the strategy lost money on every dollar actually staked.
  lines.push(`  ${padEnd('net % (fixed stake)', 22)} ${pad(num(s.netPercentageGain), 9)}`);
  lines.push(`  ${padEnd('avg % per trade', 22)} ${pad(num(s.avgPercentageGain, 3), 9)}`);
  lines.push(`  ${padEnd('profit factor', 22)} ${pad(num(s.profitFactor), 9)}`);
  // R is the comparable figure across instruments and eras: a net of 300 says nothing without the
  // risk taken for it, and under this strategy the risk varies with each instrument's volatility.
  lines.push(`  ${padEnd('average R', 22)} ${pad(num(s.avgRMultiple), 9)}`);
  lines.push(`  ${padEnd('average bars held', 22)} ${pad(num(s.avgBarsHeld, 1), 9)}`);
  lines.push('');

  // ── how the trades ended ──────────────────────────────────────────────────────────────────
  // The point of the state machine. Three stops all reporting as "stopped out" would make it
  // unmeasurable; these four counts are what move when a multiple is changed.
  const exits = s.exits || {};
  lines.push('How they ended:');
  // The name column is measured from the NAMES, not fixed at 16. A hardcoded width silently stops
  // aligning the moment a longer constant is added - which is exactly what FIB_EXTENSION_TARGET,
  // at twenty characters, did to a column built for sixteen.
  const nameWidth = Math.max(...EXIT_ORDER.map(([k]) => k.length));
  EXIT_ORDER.forEach(([key, description]) => {
    const n = exits[key] || 0;
    const share = s.trades ? ` (${((100 * n) / s.trades).toFixed(1)}%)` : '';
    lines.push(`  ${padEnd(key, nameWidth)} ${pad(n, 6)}${padEnd(share, 9)} ${description}`);
  });

  // GAPPED FILLS, counted. This is the answer to "why did a 1% stop lose 1.21R", and without it
  // the only way to find out is to open the chart and look at the bar.
  // `report.results` read directly, NOT the `results` const declared further down: naming that one
  // here reaches it before its declaration, which is the temporal-dead-zone crash App.tdz.test.js
  // exists for. Lint passed and the build compiled; only running it showed anything.
  const allTrades = Array.isArray(report.results) ? report.results : [];
  const gapped = allTrades.filter(t => t && t.gappedFill).length;
  if (gapped > 0) {
    lines.push('');
    lines.push(`${gapped} of ${allTrades.length} trades did NOT fill at the level their own rule`);
    lines.push('named — the session opened through it. Those are marked (GAP) above, and they are');
    lines.push('the only way a stop can lose more than the distance it was set at.');
  }

  if ((exits.END_OF_DATA || 0) > 0) {
    lines.push('');
    lines.push('Trades still open at the end are closed at the last close and counted, but the');
    lines.push('market did not produce them.');
  }

  // ── NO PER-ROW LISTINGS ──────────────────────────────────────────
  //
  // Two enumerations used to end this report: the most recent 25 trades by serial, and the most
  // recent 12 signals. Both are gone at the reader's request, and the request is right about what
  // this block is FOR. The Journal exists to be COMPARED - against yesterday's run, against another
  // symbol, against a different set of thresholds - and twenty-five rows of individual trades push
  // every comparable number off the top of the panel. A thousand-trade run cannot be read one row
  // at a time anyway.
  //
  // Nothing is lost that a reader cannot get better elsewhere. Every aggregate is above; the
  // per-trade detail is on the CHART, where a marker carries its serial, both ends, its R and its
  // exit reason on hover - beside the bars that produced it, which is the only place that detail
  // means anything.
  //
  // The one line kept from that block is the stale-backend warning below, and it is not a listing:
  // it fires only when the engine returned trades with NO serials, which is a fact about the build
  // rather than about the market, and this is its only home.
  const results = Array.isArray(report.results) ? report.results : [];
  if (results.length > 0 && !results.some(t => Number.isFinite(t && t.seq) && t.seq > 0)) {
    lines.push('');
    lines.push(`${results.length} trades — not numbered: this backend predates trade serials.`);
  }

  // ── WHAT THE DYNAMAN ENGINE DOES WITH THE SAME SYMBOL ──────────────────────────────────────
  //
  // Integral Delta and DynaMan are different studies, and their CTS treatments are not remotely
  // the same rule. Integral Delta puts every accepted CTS point through a trend/RSI context
  // classifier whose "no particular context" branch discards the rest — on SPY that is 279
  // accepted points down to 4 signals, which is its own documented design. DynaMan's standalone
  // CTS provider takes EVERY date and reads direction off a 5-bar SMA, which on the same symbol
  // is two orders of magnitude more trades.
  //
  // Reported side by side because the alternative is what happened: a journal showing only the
  // first, read by someone reasoning about the second, and "4 trades in 8,466 bars" looking like
  // a leak rather than like two studies disagreeing.
  //
  // These rows come from RUNNING DynaMan, never from re-deriving it here.
  lines.push(...dynaManModeLines(report.dynaManModes, report.dynaManModesError));

  return lines.join('\n');
}
